import React, { useRef, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Code,
  Eraser,
  Sparkles
} from 'lucide-react';

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  className = '',
  rows = 3
}) => {
  const editorRef = useRef(null);
  const [showHTML, setShowHTML] = React.useState(false);
  const [htmlValue, setHtmlValue] = React.useState(value);

  useEffect(() => {
    if (editorRef.current && !showHTML && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
    setHtmlValue(value || '');
  }, [value, showHTML]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange({ target: { value: editorRef.current.innerHTML } });
    }
  };

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput(); // Update the value after command execution
  };

  const createLink = () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    if (!selectedText) {
      alert('Please select some text first to create a link');
      return;
    }

    const url = prompt('Enter URL:', 'https://');
    if (url && url.trim()) {
      document.execCommand('createLink', false, url);
      handleInput();
    }
  };

  const toggleHTML = () => {
    if (showHTML) {
      onChange({ target: { value: htmlValue } });
    } else {
      setHtmlValue(editorRef.current?.innerHTML || '');
    }
    setShowHTML(!showHTML);
  };

  const ToolbarButton = ({ onClick, icon: Icon, title }) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className="p-2 hover:bg-gray-100 rounded transition-colors"
      onMouseDown={(e) => e.preventDefault()}
    >
      <Icon className="w-4 h-4 text-gray-600" />
    </button>
  );

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-col border-b border-gray-300 bg-[#f8fafc]">
        {/* Main Formatting Row */}
        <div className="flex items-center gap-1 px-2 py-1 flex-wrap border-b border-gray-200">
          <ToolbarButton onClick={() => execCommand('bold')} icon={Bold} title="Bold" />
          <ToolbarButton onClick={() => execCommand('italic')} icon={Italic} title="Italic" />
          <ToolbarButton onClick={() => execCommand('underline')} icon={Underline} title="Underline" />

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <div className="flex items-center gap-1 group">
            <span className="text-[10px] text-gray-400 font-bold uppercase ml-1">A</span>
            <input
              type="color"
              onChange={(e) => execCommand('foreColor', e.target.value)}
              className="w-4 h-4 p-0 border-none cursor-pointer bg-transparent"
              title="Text Color"
            />
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <ToolbarButton onClick={() => execCommand('justifyLeft')} icon={AlignLeft} title="Align Left" />
          <ToolbarButton onClick={() => execCommand('justifyCenter')} icon={AlignCenter} title="Align Center" />
          <ToolbarButton onClick={() => execCommand('justifyRight')} icon={AlignRight} title="Align Right" />

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <ToolbarButton onClick={() => execCommand('insertUnorderedList')} icon={List} title="Bullet List" />
          <ToolbarButton onClick={() => execCommand('insertOrderedList')} icon={ListOrdered} title="Numbered List" />

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <ToolbarButton onClick={() => execCommand('undo')} icon={Undo} title="Undo" />
          <ToolbarButton onClick={() => execCommand('redo')} icon={Redo} title="Redo" />

          <button
            type="button"
            onClick={toggleHTML}
            className={`px-2 py-1 text-[10px] font-bold rounded ${showHTML ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
            title="Toggle HTML"
          >
            HTML
          </button>

          <div className="flex-1" />
          <ToolbarButton onClick={() => execCommand('removeFormat')} icon={Eraser} title="Clear Format" />
          <ToolbarButton onClick={() => { }} icon={Sparkles} title="AI" />
        </div>

        {/* Dropdowns Row */}
        <div className="flex items-center gap-2 px-2 py-1 flex-wrap bg-white">
          <select
            onChange={(e) => execCommand('formatBlock', e.target.value)}
            className="text-xs border border-gray-200 rounded px-1 py-0.5 outline-none focus:border-blue-400"
          >
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>

          <select
            onChange={(e) => execCommand('fontName', e.target.value)}
            className="text-xs border border-gray-200 rounded px-1 py-0.5 outline-none focus:border-blue-400"
          >
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Verdana">Verdana</option>
          </select>

          <select
            onChange={(e) => execCommand('fontSize', e.target.value)}
            className="text-xs border border-gray-200 rounded px-1 py-0.5 outline-none focus:border-blue-400"
          >
            <option value="1">Small</option>
            <option value="3">Normal</option>
            <option value="5">Large</option>
            <option value="7">Extra Large</option>
          </select>
        </div>
      </div>

      {/* Editor */}
      <div className="relative">
        {showHTML ? (
          <textarea
            value={htmlValue}
            onChange={(e) => {
              setHtmlValue(e.target.value);
              onChange({ target: { value: e.target.value } });
            }}
            className="w-full px-3 py-2 outline-none font-mono text-sm bg-gray-50"
            style={{
              minHeight: `${rows * 24}px`,
              maxHeight: '400px',
              border: 'none',
              resize: 'vertical'
            }}
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            className="px-3 py-2 min-h-[60px] outline-none rounded-b-[10px] focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            style={{
              minHeight: `${rows * 24}px`,
              maxHeight: '400px',
              overflowY: 'auto'
            }}
            data-placeholder={placeholder}
            suppressContentEditableWarning
          />
        )}
      </div>

      <div className="px-2 py-1 bg-gray-100 border-t border-gray-300 text-[10px] text-gray-500 flex justify-between">
        <span>Path: p</span>
        <span className="cursor-nwse-resize opacity-50">◢</span>
      </div>

      <style>{`
        [contentEditable="true"]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        [contentEditable="true"] {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        [contentEditable="true"] ul {
          list-style-type: disc;
          margin-left: 20px;
          padding-left: 20px;
        }
        [contentEditable="true"] ol {
          list-style-type: decimal;
          margin-left: 20px;
          padding-left: 20px;
        }
        [contentEditable="true"] li {
          margin: 4px 0;
        }
        [contentEditable="true"] a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }
        [contentEditable="true"] a:hover {
          color: #1d4ed8;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
